import React from "react";
import classNames from "classnames/bind";
import styles from "./styles.module.scss";
import {
  HandChangeBanner,
  NeedsYouNotice,
  SkippedDayBanner,
} from "components/HandChangeBanner";
import {
  isAllergyMealTime,
  mealKeyOfMealTime,
  needsYouText,
} from "components/StandingOrder/format";
import { TStatusDay, TStatusMeal } from "components/StandingOrder/types";

const cn = classNames.bind(styles);

/** What the daily order screen knows about the day's standing orders. */
export type TStandingView = {
  day: TStatusDay;
  /** false once the order deadline has passed */
  canGoBack: boolean;
  goBack: (meal?: string) => void;
  error: string | null;
};

export const buildStandingView = (
  standing: {
    enabled: boolean;
    day: TStatusDay | null;
    error: string | null;
    goBack: (meal?: string) => void;
  },
  canOrder: boolean | undefined
): TStandingView | undefined =>
  standing.enabled && standing.day
    ? {
        day: standing.day,
        canGoBack: canOrder !== false && !standing.day.locked,
        goBack: standing.goBack,
        error: standing.error,
      }
    : undefined;

const meals = (view: TStandingView): TStatusMeal[] =>
  (view.day.meals || []).filter((m) => m.state !== "none");

const isSkippedDay = (view: TStandingView) =>
  meals(view).length > 0 && meals(view).every((m) => m.state === "skipped");

/** Day-level: the "Filled in..." line, the skipped-day banner, a Go back error. */
const StandingOrderDayNotes: React.FC<{ view?: TStandingView }> = ({ view }) => {
  if (!view) return null;
  // a closed (past-deadline) day is quiet: the deadline message already says
  // it is closed, and nothing here could be acted on
  if (!view.canGoBack)
    return view.error ? <div className={cn("error")}>{view.error}</div> : null;
  const filled = meals(view).some(
    (m) => m.state === "standing" || m.state === "needs_you"
  );
  return (
    <>
      {view.error && <div className={cn("error")}>{view.error}</div>}
      {isSkippedDay(view) && (
        <SkippedDayBanner
          canGoBack={view.canGoBack}
          onGoBack={() => view.goBack()}
        />
      )}
      {filled && (
        <div className={cn("filled")}>
          Filled in from your standing order.
          {view.canGoBack &&
            " Change anything before the deadline, or press Skip Day."}
        </div>
      )}
    </>
  );
};

/** Under a meal's header: the hand-change banner or the Needs-you notice. */
export const StandingMealNotice: React.FC<{
  view?: TStandingView;
  mealTimeName: string;
}> = ({ view, mealTimeName }) => {
  if (!view || !view.canGoBack || isAllergyMealTime(mealTimeName) || isSkippedDay(view))
    return null;
  const key = mealKeyOfMealTime(mealTimeName);
  const entry = key && meals(view).find((m) => m.meal === key);
  if (!key || !entry) return null;
  if (entry.state === "changed") {
    return (
      <HandChangeBanner
        meal={key}
        canGoBack={view.canGoBack}
        onGoBack={() => view.goBack(key)}
      />
    );
  }
  if (entry.state === "needs_you" && entry.unmet && entry.unmet.length) {
    return <NeedsYouNotice text={needsYouText(entry.unmet)} />;
  }
  return null;
};

/** Under a dish: "24 standard + 2 vegetarian", for standing and needs-you meals only. */
export const StandingDishNote: React.FC<{
  view?: TStandingView;
  dailyMenuToMenuItemId: number | string;
}> = ({ view, dailyMenuToMenuItemId }) => {
  if (!view) return null;
  const texts: string[] = [];
  meals(view).forEach((m) => {
    if (m.state !== "standing" && m.state !== "needs_you") return;
    (m.notes || []).forEach((n) => {
      if (String(n.dailyMenuItemId) === String(dailyMenuToMenuItemId)) {
        texts.push(n.text);
      }
    });
  });
  return texts.length ? (
    <span className={cn("dish-note", "standing-dish-note")}>
      {texts.join(" + ")}
    </span>
  ) : null;
};

export default StandingOrderDayNotes;
